import { api } from '../api/client';

/** Avisos al celular (Web Push): suscribir o quitar ESTE dispositivo. */
export type PushSupport = 'ok' | 'unsupported' | 'ios-install' | 'server-off';

const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isStandalone = () => window.matchMedia?.('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;

export function pushSupport(publicKey: string | null): PushSupport {
  if (!publicKey) return 'server-off';
  const capable = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  // En iPhone los avisos solo funcionan con KEORA agregada a la pantalla de inicio (iOS 16.4+).
  if (isIos() && !isStandalone()) return 'ios-install';
  return capable ? 'ok' : 'unsupported';
}

async function registration(): Promise<ServiceWorkerRegistration> {
  return (await navigator.serviceWorker.getRegistration()) ?? navigator.serviceWorker.register('/sw.js');
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  return reg ? reg.pushManager.getSubscription() : null;
}

/** ¿Este dispositivo recibe los avisos de la Persona con sesión? */
export async function isPushOn(): Promise<boolean> {
  const sub = await currentSubscription();
  if (!sub || Notification.permission !== 'granted') return false;
  return (await api.post<{ subscribed: boolean }>('/notifications/push/subscriptions/status', { endpoint: sub.endpoint })).subscribed;
}

const keyBytes = (b64u: string) => {
  const b64 = b64u.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (b64u.length % 4)) % 4);
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
};

/** Quien desactivó los avisos en este dispositivo no vuelve a recibirlos solos al abrir la app. */
const OFF_KEY = 'keora.push.off';
const remember = (off: boolean) => {
  try {
    if (off) localStorage.setItem(OFF_KEY, '1');
    else localStorage.removeItem(OFF_KEY);
  } catch {
    /* sin almacenamiento */
  }
};
const turnedOff = () => {
  try {
    return localStorage.getItem(OFF_KEY) === '1';
  } catch {
    return false;
  }
};

export async function enablePush(publicKey: string): Promise<void> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('No diste permiso para mostrar avisos. Actívalo en la configuración del navegador para este sitio.');
  await subscribeDevice(publicKey);
  remember(false);
}

/** Suscribe este dispositivo (ya con permiso) y lo registra a nombre de quien tiene la sesión. */
async function subscribeDevice(publicKey: string): Promise<void> {
  const reg = await registration();
  await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (sub && sub.options.applicationServerKey) {
    const current = new Uint8Array(sub.options.applicationServerKey);
    const wanted = keyBytes(publicKey);
    if (current.length !== wanted.length || current.some((b, i) => b !== wanted[i])) {
      await sub.unsubscribe();
      sub = null;
    }
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) });
  await api.post('/notifications/push/subscriptions', sub.toJSON());
}

/**
 * Quita este dispositivo de los avisos de la Persona. Al cerrar sesión solo se desvincula (quien
 * entre después lo recibe solo); «Desactivar avisos aquí» además lo apaga en este dispositivo.
 */
export async function disablePush({ turnOff = false } = {}): Promise<void> {
  if (turnOff) remember(true);
  synced = null;
  const sub = await currentSubscription();
  if (!sub) return;
  await api.post('/notifications/push/subscriptions/remove', { endpoint: sub.endpoint }).catch(() => undefined);
}

let synced: { personId: string; done: Promise<void> } | null = null;

/**
 * Al abrir la app: si este navegador ya dio permiso de avisos para KEORA, el dispositivo queda
 * suscrito a nombre de quien tiene la sesión (y se renueva si la suscripción cambió), sin preguntar.
 * Una vez por Persona y carga de la página.
 */
export function syncPush(personId: string): Promise<void> {
  if (synced?.personId !== personId) synced = { personId, done: syncOnce().catch(() => undefined) };
  return synced.done;
}

/** Para pruebas: olvida la sincronización hecha. */
export function resetPushSync(): void {
  synced = null;
}

async function syncOnce(): Promise<void> {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted' || turnedOff()) return;
  const { publicKey } = await api.get<{ publicKey: string | null }>('/notifications/push/key');
  if (pushSupport(publicKey) !== 'ok') return;
  await subscribeDevice(publicKey!);
}
